import React, {
  useEffect, useRef, useState, useContext,
} from 'react';
import styled from 'styled-components';
import Skeleton from 'react-loading-skeleton';
// eslint-disable-next-line import/no-extraneous-dependencies
import Grid from '@mui/material/Grid';
import PropTypes from 'prop-types';
import Typography from '@digital-hig/mui/dist/Typography';
import { useSelector } from 'react-redux';
import Header from '../common/header';
import RelatedLearning from './relatedLearning';
import {
  ENVIRONMENT, PAGETYPE_MODULE, LRS_VERB_STARTED, LRS_VERB_COMPLETED,
} from '../common/constants';
import Overview from '../common/overview';
import EducatorResources from '../common/educatorResources';
import Announcements from '../common/announcements';
import LeftNav from '../common/leftNav';
import ThinHeader from './thinHeader';
import EnvContext from '../common/envContext';
import { LRSHasVerbRecordForID, mapUnitData } from '../common/utilities';
import OutlineUnits from '../common/outlineUnits';
import EduResDownloads from '../common/eduResDownloads';
import { getUserId, isAuthChecked, hasFetchedLearningRecords } from '../store/userSlice';
import { hasCompletedRecord } from '../common/userUtils';
import NextSteps from './nextSteps';
import { formatString } from '../common/analyticsApi';

const ModuleBody = ({
  moduleData, showLoader, learningRecords,
}) => {
  const [leftNav, setLeftNav] = useState([]);
  const overviewRef = useRef();
  const downloadsRef = useRef();
  const outlineRef = useRef();
  const educatorResRef = useRef();
  const announcementsRef = useRef();
  const leftNavRef = useRef();
  const relatedRef = useRef();
  const headerRef = useRef(null);
  const thinHeaderRef = useRef(null);
  const [thinHeaderVisible, setIsThinHeaderVisible] = useState(false);
  const [curSelectedItem, setCurSelectedItem] = useState('');
  const envContext = useContext(EnvContext);
  const env = envContext[ENVIRONMENT];
  const uid = useSelector(getUserId);
  const authChecked = useSelector(isAuthChecked);
  const recordChecked = useSelector(hasFetchedLearningRecords);
  const [isModuleStarted, setIsModuleStarted] = useState(false);
  const [isModuleCompleted, setIsModuleCompleted] = useState(false);

  const [unitData, setUnitData] = useState(mapUnitData({ moduleData, learningRecords, env }));

  const headerInView = () => {
    let headerContainer = '';
    if (headerRef.current) {
      headerContainer = headerRef.current.firstChild;
      const headerTopPos = headerContainer?.offsetTop;
      const headerHeight = headerContainer?.offsetHeight;
      const headerPaddings = parseFloat(window.getComputedStyle(headerContainer).paddingTop) + parseFloat(window.getComputedStyle(headerContainer).paddingBottom);
      const isPassedTheshold = window.scrollY >= ((headerTopPos + headerHeight) + (headerPaddings));
      return isPassedTheshold;
    }
    return false;
  };

  const scroller = () => {
    if (headerInView()) {
      setIsThinHeaderVisible(true);
    } else {
      setIsThinHeaderVisible(false);
    }
  };

  useEffect(() => {
    if ((authChecked && !uid) || recordChecked) {
      const mappedData = mapUnitData({ moduleData, learningRecords, env });
      setUnitData(mappedData);
      const started = LRSHasVerbRecordForID({
        learningRecords, id: moduleData.id, env, verb: LRS_VERB_STARTED,
      });
      if (started !== isModuleStarted) {
        setIsModuleStarted(started);
      }
      const completed = LRSHasVerbRecordForID({
        learningRecords, id: moduleData.id, env, verb: LRS_VERB_COMPLETED,
      });
      if (completed !== isModuleCompleted) {
        setIsModuleCompleted(completed);
      }
    }
  }, [moduleData, learningRecords, uid, recordChecked]);

  useEffect(() => {
    if (headerRef.current !== null && thinHeaderRef.current !== null) {
      window.scrollTo(0, 0);
      window.addEventListener('scroll', scroller);
    }
    return () => {
      window.removeEventListener('scroll', scroller);
    };
  }, [headerRef.current, thinHeaderRef.current]);

  const buildLeftNav = () => {
    const navMap = [
      {
        menuItemName: 'Module overview',
        render: !!moduleData.overview,
        component: Overview,
        data: moduleData.overview,
        title: 'Module overview',
        ref: overviewRef,
      },
      {
        menuItemName: 'Module downloads',
        render: !!moduleData.dataSets,
        component: ({ sectionRef, data, sectionId }) => (
          <div ref={sectionRef} data-id={sectionId}>
            <Typography component="h2" variant="headline-medium">
              Module downloads
            </Typography>
            <EduResDownloads
              downloads={data}
              title="These downloadable resources will be used to complete units in this module:"
              btnLabel="Download all resources"
              analyticsPrefix="moduleDownloads"
            />
          </div>
        ),
        data: moduleData.dataSets,
        ref: downloadsRef,
      },
      {
        menuItemName: 'Module outline',
        render: moduleData?.units?.length !== 0,
        component: ({ sectionRef, data, sectionId }) => (
          <div ref={sectionRef} data-id={sectionId}>
            <Grid container flexDirection="column">
              <Grid item>
                <Typography component="h2" variant="headline-medium">Module outline</Typography>
              </Grid>
              <Grid item>
                <OutlineUnits units={data} analyticsPrefix={`module overview:na:na:${formatString(moduleData?.title)}:${moduleData?.id}:`} />
              </Grid>
            </Grid>
          </div>
        ),
        data: unitData,
        ref: outlineRef,
      },
      {
        menuItemName: 'Teaching supplements',
        render: !!moduleData?.educatorResources?.content?.length || !!moduleData?.educatorResources?.downloads?.length,
        component: EducatorResources,
        data: moduleData.educatorResources,
        ref: educatorResRef,
      },
      {
        menuItemName: 'Announcements',
        render: !!moduleData.announcements,
        component: Announcements,
        data: moduleData.announcements?.announcements,
        ref: announcementsRef,
      },
      {
        menuItemName: 'Related learning',
        render: ((moduleData?.relatedLearning?.length !== 0) || moduleData?.relatedTags),
        component: RelatedLearning,
        data: { relatedLearning: moduleData?.relatedLearning, relatedTags: moduleData?.relatedTags },
        ref: relatedRef,
      },
    ];
    setLeftNav(navMap);
  };

  if (overviewRef.current !== null && leftNav.length === 0) {
    buildLeftNav();
  }

  useEffect(() => {
    if (overviewRef.current !== null) {
      buildLeftNav();
    }
  }, [moduleData, overviewRef, unitData]);

  const handleScroll = () => {
    const availableTabs = leftNav.filter((tab) => tab.render);
    availableTabs.forEach((section, index) => {
      if (section?.ref?.current) {
        const windowScrollY = window.scrollY + 250; // 250 is the Threshold from top TODO::this needs to be calcualted
        const sectionTopPos = section.ref.current.offsetTop;
        const sectionBottomPos = (section.ref.current.offsetTop + section.ref.current.offsetHeight);

        if (windowScrollY >= sectionTopPos && windowScrollY <= sectionBottomPos) {
          setCurSelectedItem(index);
        }
      }
    });
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [leftNav]);

  return (
    <>
      {showLoader ? (
        <>
          <div className="loader">
            <Header isLoading={showLoader} />
          </div>

        </>
      ) : (
        <>
          { Object.keys(moduleData).length > 0
            ? (
              <>

                <Header
                  pageType={PAGETYPE_MODULE}
                  data={moduleData}
                  headerRef={headerRef}
                  isCompleted={isModuleCompleted}
                  isStarted={isModuleStarted}
                />
                <ThinHeader
                  thinHeaderRef={thinHeaderRef}
                  data={moduleData}
                  isVisible={thinHeaderVisible}
                  isCompleted={isModuleCompleted}
                  isStarted={isModuleStarted}
                  analyticsArea="moduleThinHeader"
                />
                { authChecked && hasCompletedRecord({ learningRecords, id: moduleData.id, env }) && (
                  <NextSteps data={moduleData} learningRecords={learningRecords} />
                )}

                {showLoader ? <Skeleton height={72} width={250} />
                  : leftNav && leftNav.length !== 0 && (
                    <RootElement className="maxWidth">
                      <Grid container>
                        <Grid item xs={12} lg={3}>
                          <LeftNav
                            navItems={leftNav.map((item) => ({ menuItemName: item.menuItemName, render: item.render, ref: item.ref }))}
                            curSelectedItem={curSelectedItem}
                            isSticky={thinHeaderVisible}
                            thinHeaderRef={thinHeaderRef}
                            isThinHeaderVisible={thinHeaderVisible}
                            leftNavRef={leftNavRef}
                          />
                        </Grid>
                        <Grid item xs={12} lg={9}>
                          {leftNav.map((item) => item.render
                          && (
                            <Grid key={item.menuItemName} ref={item.ref} className="module-section">
                              <item.component data={item.data} sectionRef={item.ref} sectionId={item.menuItemName} title={item.title} />
                            </Grid>
                          ))}
                        </Grid>
                      </Grid>
                    </RootElement>
                  )}
              </>
            ) : (<></>)}
        </>
      )}
    </>
  );
};

const RootElement = styled.div`
  .MuiGrid-root.MuiGrid-item{
    padding-left: 0;
  }
   
    margin: 18px auto !important;
    
    .module-section {
      margin-bottom: 62px;
    }
  @media only screen and (min-width: 1040px){
    margin: 58px auto 0 auto !important;
  }
`;

ModuleBody.propTypes = {
  showLoader: PropTypes.bool.isRequired,
  moduleData: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    units: PropTypes.arrayOf(PropTypes.shape({})),
    educatorResources: PropTypes.shape({
      content: PropTypes.arrayOf(),
      downloads: PropTypes.arrayOf(),
    }),
    announcements: PropTypes.shape({ announcements: PropTypes.string }),
    overview: PropTypes.string,
    dataSets: PropTypes.arrayOf(PropTypes.string),
    relatedLearning: PropTypes.arrayOf({}),
    relatedTags: PropTypes.shape({}),
  }).isRequired,
  thinHeaderRef: PropTypes.shape({}).isRequired,
  learningRecords: PropTypes.shape({}).isRequired,
};

export default React.memo(ModuleBody);
